import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import useBundleTranslation from 'i18n';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { useQueryClient } from '@tanstack/react-query';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { useFormState } from 'react-hook-form';
import { instance } from 'api/api';
import IconHandler, { IconType } from 'components/common/icon/IconHandler';

interface RestoreDefaultsFormButtonProps extends FormControlProps {
    url: string;
    reloadGrids: string[];
    defaultValues?: any;
    reloadFields: string[];
    onRestore?: () => void;
    icon?: boolean | IconType;
    buttonLabel?: string;
}

export default function RestoreDefaultsFormButton({
    controlProps,
    elementProps,
}: FormElementControlPropsType<RestoreDefaultsFormButtonProps>) {
    const { t } = useBundleTranslation(elementProps?.translationNS);
    const [isLoading, toggleLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const { isDirty, dirtyFields } = useFormState({ control: controlProps.form.hookFormControl });
    const queryClient = useQueryClient();
    const defaultValues = controlProps.form.hookFormGetValues('default_values');
    const formIsDirty = isDirty && Object.keys(dirtyFields).length > 0;

    const handleClick = async () => {
        setShowConfirm(true);
    };

    const confirmSettings: PopupSettings = {
        title: t('button_restore.confirmation_header'),
        textOK: t('button_restore.confirmation_button'),
        noOk: formIsDirty,
    };

    const onConfirmed = () => {
        toggleLoading(true);
        instance
            .get(controlProps.url)
            .then(() => {
                if (controlProps.defaultValues) {
                    Object.keys(controlProps.defaultValues).forEach((fieldName) => {
                        controlProps.form.hookFormSetValue(fieldName, controlProps.defaultValues[fieldName], {
                            shouldDirty: false,
                        });
                    });
                }

                if (controlProps.reloadFields.length > 0) {
                    if (!defaultValues) {
                        console.warn('RestoreDefaults: default_values data does not exist');
                    } else {
                        controlProps.reloadFields.forEach((field) => {
                            controlProps.form.hookFormSetValue(field, defaultValues[field], {
                                shouldDirty: false,
                            });
                        });
                    }
                }

                if (controlProps.reloadGrids && controlProps.reloadGrids.length > 0) {
                    controlProps.reloadGrids.forEach((gridName: string) => {
                        queryClient.invalidateQueries({ queryKey: [gridName] });
                    });
                }
                if (controlProps.onRestore) {
                    controlProps.onRestore();
                }
            })
            .finally(() => {
                toggleLoading(false);
                setShowConfirm(false);
            });
    };

    const iconData = !controlProps.icon ? { type: 'mi', value: 'refresh-cw' } : controlProps.icon;

    return (
        <>
            <Box alignContent={'end'}>
                <Button
                    data-test={controlProps.uid}
                    onClick={handleClick}
                    variant={'outlined'}
                    startIcon={controlProps.icon !== false ? <IconHandler icon={iconData} /> : undefined}
                    sx={{ maxHeight: '28px' }}
                >
                    {t(controlProps.buttonLabel ? controlProps.buttonLabel : 'restore_default')}
                </Button>
            </Box>
            {isLoading && <LoadingPlaceholder />}
            {showConfirm && (
                <Popup
                    key={'variable-delete-confirm'}
                    settings={confirmSettings}
                    open={showConfirm}
                    onHide={() => setShowConfirm(false)}
                    onConfirm={onConfirmed}
                    maxWidth={'popupSm'}
                >
                    <Typography>
                        {formIsDirty
                            ? t('button_restore.confirmation_form_dirty_message')
                            : t('button_restore.confirmation_message')}
                    </Typography>
                </Popup>
            )}
        </>
    );
}

export class RestoreDefaultsFormButtonBuilder extends FormComponentBuilder {
    prepareProps(): RestoreDefaultsFormButtonProps {
        return {
            ...this.controlProps,
            url: this.elementProps.componentProps?.url ?? '',
            defaultValues: this.elementProps.componentProps?.defaultValues ?? undefined,
            reloadGrids: this.elementProps.componentProps?.reloadGrids ?? [],
            reloadFields: this.elementProps.componentProps?.reloadFields ?? [],
            onRestore: this.elementProps.componentProps?.onRestore ?? undefined,
            icon: this.elementProps.componentProps?.icon ?? undefined,
            buttonLabel: this.elementProps.componentProps?.buttonLabel ?? '',
        };
    }
}
